import { mainApi } from '../../../../api/mainApi';
import { AccessState } from '../../../../types';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { CollaboratorPeriod } from '../interfaces/collaborator-periods.interfaces';
import {
  Collaborator,
  CollaboratorPayload,
  CollaboratorsResponse,
  ICredentials,
} from '../interfaces/collaborators.interfaces';
import { DisabilityType } from '../interfaces/disabilityType.interfaces';
import { Person, PersonPayload } from '../interfaces/person.interface';

import { setCollaboratorsList } from './collaboratorsSlice';
import { CoursesCollaboratorResponse } from '../../../maintenance/courses/interfaces/coursesCollaborator.interface';
import {
  CollaboratorsAdvisors,
  CollaboratorsProjects,
} from '../interfaces/collaborator-projects.interfaces';
import { CollaboradorByOfferta } from '../../../evaluations/modules/maintenance/form/interfaces/collaboratorByOffer.interface';

export const collaboratorsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: [
    'Collaborators',
    'CollaboratorsTypeList',
    'CollaboratorsByEducationLevel',
    'CollaboratorsAvailables',
    'CollaboratorsByProjects',
    'CollaboratorsAdvisors',
  ],
});
// Define a service using a base URL and expected endpoints
export const collaboratorsApi = collaboratorsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all users
     */
    getCollaborators: builder.query<
      CollaboratorsResponse,
      {
        profileId?: number;
        page: number;
        perPage: number;
        search: string;
        coll_type?: string;
        statusId?: number;
        isFaculty?: number;
        sort?: string;
        type_sort?: string;
        joint_academic_id?: number;
        education_level_id?: number;
      }
    >({
      queryFn: async (
        {
          profileId,
          page,
          search,
          perPage,
          coll_type,
          statusId,
          isFaculty,
          sort,
          type_sort,
          joint_academic_id,
          education_level_id,
        },
        { dispatch, getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<CollaboratorsResponse>('/collaborators', {
            params: {
              user_profile_id,
              page,
              size: perPage,
              search,
              ...(statusId && { status_id: statusId }),
              ...(coll_type && { coll_type }),
              ...(isFaculty && { isFaculty }),
              ...(sort && { sort }),
              ...(type_sort && { type_sort }),
              ...(joint_academic_id && { joint_academic_id }),
              ...(education_level_id && { education_level_principal_id: education_level_id }),
            },
          });

          dispatch(setCollaboratorsList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Collaborators', id } as const)),
              { type: 'Collaborators', id: 'LIST' },
            ]
          : [{ type: 'Collaborators', id: 'LIST' }],
    }),
    /**
     * @GET all users
     */
    getCollaboratorsAll: builder.query<
      Collaborator[],
      {
        profileId?: number;
        coll_type?: string;
        statusId?: number;
        isFaculty?: number;
        sort?: string;
        type_sort?: string;
        joint_academic_id?: number;
        education_level_id?: number;
      }
    >({
      queryFn: async (
        {
          profileId,
          coll_type,
          statusId,
          isFaculty,
          sort,
          type_sort,
          joint_academic_id,
          education_level_id,
        },
        { dispatch, getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Collaborator[]>('/collaborators', {
            params: {
              user_profile_id,
              data: 'all',
              ...(statusId && { status_id: statusId }),
              ...(coll_type && { coll_type }),
              ...(isFaculty && { isFaculty }),
              ...(sort && { sort }),
              ...(type_sort && { type_sort }),
              ...(joint_academic_id && { joint_academic_id }),
              ...(education_level_id && { education_level_principal_id: education_level_id }),
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'Collaborators', id: 'LIST' }],
    }),
    /**
     * @GET all users
     */
    getCollaboratorsByProjects: builder.query<
      CollaboratorsProjects,
      {
        profileId?: number;
        page: number;
        perPage: number;
        search: string;
        coll_type?: string;
        statusId?: number;
        isFaculty?: number;
      }
    >({
      queryFn: async (
        { profileId, page, search, perPage, coll_type, statusId, isFaculty },
        { dispatch, getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<CollaboratorsProjects>(
            '/projects-research-collaborators',
            {
              params: {
                user_profile_id,
                page,
                size: perPage,
                type_sort: 'desc',
                search,
                ...(statusId && { status_id: statusId }),
                ...(coll_type && { coll_type }),
                ...(isFaculty && { isFaculty }),
              },
            }
          );

          // dispatch(setCollaboratorsList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ collaborator_id }) =>
                  ({ type: 'CollaboratorsByProjects', collaborator_id } as const)
              ),
              { type: 'CollaboratorsByProjects', id: 'LIST' },
            ]
          : [{ type: 'CollaboratorsByProjects', id: 'LIST' }],
    }),

    getCollaboratorsAdvisor: builder.query<
      CollaboratorsAdvisors,
      {
        profileId?: number;
        page: number;
        perPage: number;
        search: string;
      }
    >({
      queryFn: async ({ profileId, page, search, perPage }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<CollaboratorsAdvisors>(
            '/collaborators/academic-advisor',
            {
              params: {
                user_profile_id,
                page,
                size: perPage,
                type_sort: 'desc',
                search,
              },
            }
          );

          // dispatch(setCollaboratorsList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'CollaboratorsAdvisors', id: 'LIST' }],
    }),

    /**
    //  * @GET collaborator by id
    //  */
    getCollaborator: builder.query<Collaborator, { collaboratorId: number }>({
      queryFn: async ({ collaboratorId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<Collaborator>(`/collaborators/${collaboratorId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET Get availibles collaborators by education level
     */
    getAvailablesCollaboratorsByEdLvl: builder.query<
      Collaborator[],
      { profileId: number; edLvlId: number }
    >({
      queryFn: async ({ profileId, edLvlId }) => {
        try {
          const { data } = await mainApi.get<CollaboratorsResponse>(
            `/education-levels/${edLvlId}/collaborators`,
            {
              params: {
                user_profile_id: profileId,
              },
            }
          );

          const dataAll = Array.isArray(data.data) ? data.data : [];

          return { data: dataAll };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [
        { type: 'CollaboratorsAvailables', id: 'COL_CMBO_LIST' },
      ],
    }),

    /**
     * @GET all collaborator by coll_type
     */
    getCollaboratorTypes: builder.query<
      Collaborator[] | [],
      { profileId: number; coll_type: string; isFaculty?: boolean; status_id?: number }
    >({
      queryFn: async ({ profileId, coll_type, isFaculty, status_id }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<Collaborator[]>(`/collaborators`, {
            params: {
              user_profile_id: profileId,
              coll_type: coll_type,
              isFaculty,
              status_id,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'CollaboratorsTypeList', id } as const)),
              { type: 'CollaboratorsTypeList', id: 'LIST' },
            ]
          : [{ type: 'CollaboratorsTypeList', id: 'LIST' }],
    }),

    /**
     * @GET all collaborator by education level without collective added
     */
    getCollaboratorByEducationLevel: builder.query<
      Collaborator[],
      { profileId: number; educationlevel: number; type_collaborator: string }
    >({
      queryFn: async ({ profileId, educationlevel, type_collaborator }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<Collaborator[]>(
            `/collaborators/joint-academic/${educationlevel}`,
            {
              params: {
                user_profile_id: profileId,
                size: 0,
                data: 'all',
                education_level_id: educationlevel,
                type_collaborator,
              },
            }
          );
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'CollaboratorsByEducationLevel', id } as const)),
              { type: 'CollaboratorsByEducationLevel', id: 'LIST' },
            ]
          : [{ type: 'CollaboratorsByEducationLevel', id: 'LIST' }],
    }),

    /**
     * @POST create collaborator
     */
    addCollaborator: builder.mutation<
      Collaborator,
      { profileId: number; colaboratorPayload: Partial<CollaboratorPayload> }
    >({
      queryFn: async ({ profileId, colaboratorPayload }) => {
        try {
          const { data } = await mainApi.post<Collaborator>('/collaborators', {
            user_profile_id: profileId,
            ...colaboratorPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Collaborators', id: 'LIST' },
        { type: 'CollaboratorsTypeList', id: 'LIST' },
        { type: 'CollaboratorsByEducationLevel', id: 'LIST' },
      ],
    }),

    /**
     * @PUT update collaborator
     */
    updateCollaborator: builder.mutation<
      Collaborator,
      {
        profileId: number;
        collaboratorId: number;
        collaboratorPayload: Partial<Collaborator>;
      }
    >({
      queryFn: async ({ profileId, collaboratorId, collaboratorPayload }) => {
        try {
          const { data } = await mainApi.put<Collaborator>(`/collaborators/${collaboratorId}`, {
            user_profile_id: profileId,
            ...collaboratorPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Collaborators', id: 'LIST' },
        { type: 'CollaboratorsTypeList', id: 'LIST' },
        { type: 'CollaboratorsByEducationLevel', id: 'LIST' },
      ],
    }),

    /**
     * @DELETE remove period
     */
    deleteCollaborator: builder.mutation<Collaborator, { profileId: number; collabId: number }>({
      queryFn: async ({ profileId, collabId }) => {
        try {
          const { data } = await mainApi.delete(`collaborators/${collabId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Collaborators', id: 'LIST' },
        { type: 'CollaboratorsTypeList', id: 'LIST' },
        { type: 'CollaboratorsByEducationLevel', id: 'LIST' },
      ],
    }),

    /**
     * @GET stage list to combo box
     */
    getDisabilityTypes: builder.query<DisabilityType[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get('/type-disabilities', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET person by id
     */
    //considerar crear carpetas aunque sea solo los apiSlice para los endpoints que
    //no tengan mantenimiento visual, para centralizar los endpoints
    getPersonCollaborator: builder.query<Person, { profileId: number; personId: number }>({
      queryFn: async ({ profileId, personId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<Person>(`/persons/${personId}`, {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Collaborators', profileId }],
    }),

    /**
    //  * @GET collaborator periods current
    //  */
    getCollaboratorPeriodsAvailables: builder.query<
      CollaboratorPeriod[],
      { collaboratorId: number; userProfileId: number; anio?: number }
    >({
      queryFn: async ({ userProfileId, anio, collaboratorId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<CollaboratorPeriod[]>(
            `/collaborators/${collaboratorId}/periods`,
            {
              params: {
                user_profile_id: userProfileId,
                anio,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
    //  * @GET collaborator courses
    //  */
    getCollaboratorCourses: builder.query<
      CoursesCollaboratorResponse,
      { profileId: number; collaboratorId: number; page: number; perPage: number; search: string }
    >({
      queryFn: async ({ profileId, collaboratorId, page, perPage, search }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<CoursesCollaboratorResponse>(
            `/collaborators/${collaboratorId}/courses`,
            {
              params: {
                user_profile_id: profileId,
                page,
                size: perPage,
                type_sort: 'desc',
                search,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
    //  * @GET collaborator by ci
    //  */
    getCollaboratorByCi: builder.query<Collaborator, { profileId: number; ci: string }>({
      queryFn: async ({ profileId, ci }) => {
        try {
          const { data } = await mainApi.get<Collaborator>(`/collaborators/per-ci/${ci}`, {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @PUT update person
     * @Permission persons-actualizar-person
     */
    updatePersonCollaborator: builder.mutation<
      PersonPayload,
      {
        profileId: number;
        personId: number;
        personPayload: Partial<PersonPayload>;
      }
    >({
      queryFn: async ({ profileId, personId, personPayload }) => {
        try {
          const { data } = await mainApi.put<PersonPayload>(`/persons/${personId}`, {
            user_profile_id: profileId,
            ...personPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Collaborators', id: 'LIST' }],
    }),

    /**
     * @POST create collaborator
     */
    addCredential: builder.mutation<
      ICredentials,
      { userId: number; credPayload: Partial<ICredentials> }
    >({
      queryFn: async ({ userId, credPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<ICredentials>(
            `/users/${userId}/virtual-meeting/credentials`,
            {
              user_profile_id,
              ...credPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Collaborators', id: 'LIST' },
        { type: 'CollaboratorsTypeList', id: 'LIST' },
        { type: 'CollaboratorsByEducationLevel', id: 'LIST' },
      ],
    }),
    /**
     * @GET all collaborators
     */
    getAllCollaborators: builder.query<
      CollaboratorsResponse,
      {
        user_profile_id: number;
      }
    >({
      queryFn: async ({ user_profile_id }) => {
        try {
          const { data } = await mainApi.get<CollaboratorsResponse>('/collaborators?data=all', {
            params: {
              user_profile_id,
            },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getAlLCollaboratorByEducationLevelIdByOfferId: builder.query<
      CollaboradorByOfferta[],
      {
        user_profile_id: number | string;
        education_level_principal_id: number | string;
        offer_id: number | string;
      }
    >({
      queryFn: async ({ user_profile_id, education_level_principal_id, offer_id }) => {
        try {
          const { data } = await mainApi.get<CollaboradorByOfferta[]>(
            `/education-levels-by-collaborator-offer`,
            {
              params: {
                user_profile_id,
                education_level_principal_id,
                offer_id,
              },
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useAddCollaboratorMutation,
  useUpdateCollaboratorMutation,
  useGetCollaboratorPeriodsAvailablesQuery,
  useDeleteCollaboratorMutation,
  useGetCollaboratorsQuery,
  useLazyGetCollaboratorsQuery,
  useGetDisabilityTypesQuery,
  useGetAvailablesCollaboratorsByEdLvlQuery,
  useLazyGetAvailablesCollaboratorsByEdLvlQuery,
  useUpdatePersonCollaboratorMutation,
  useGetPersonCollaboratorQuery,
  useGetCollaboratorTypesQuery,
  useGetCollaboratorByEducationLevelQuery,
  useGetCollaboratorByCiQuery,
  useLazyGetCollaboratorByCiQuery,
  useGetCollaboratorQuery,
  useLazyGetCollaboratorQuery,
  useLazyGetCollaboratorsByProjectsQuery,
  useLazyGetCollaboratorsAdvisorQuery,
  useGetCollaboratorCoursesQuery,
  useAddCredentialMutation,
  useGetAllCollaboratorsQuery,
  useLazyGetCollaboratorsAllQuery,
  useLazyGetAlLCollaboratorByEducationLevelIdByOfferIdQuery,
} = collaboratorsApi;
